/**
 * `warehouses.*` — godown/location master CRUD.
 *
 * Tenant-scoped mutable master data. Soft-deactivate via isActive.
 * Mirrors the items.* contract shape.
 */
import { z } from "zod";
import { uuid } from "../_common.js";

export const warehouseType = z.enum(["physical", "virtual", "job_work"]);
export type WarehouseType = z.infer<typeof warehouseType>;

const baseFields = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  code: z.string().trim().max(20).optional(),
  description: z.string().trim().min(1).max(300).optional(),
  warehouseType: warehouseType.optional(),
  isActive: z.boolean().optional(),
  /** Mark as the company's default receiving/dispatch warehouse. At most one can be default. */
  isDefault: z.boolean().optional(),
});

export const createInput = baseFields.extend({ name: z.string().trim().min(1).max(100) });
export const createOutput = z.object({ id: uuid });

export const getInput = z.object({ id: uuid });

export const warehouseRow = z.object({
  id: uuid,
  name: z.string(),
  code: z.string().nullable(),
  description: z.string().nullable(),
  warehouseType,
  isActive: z.boolean(),
  isDefault: z.boolean(),
  itemCount: z.number().int(),          // stocked item count (from stock_balances join)
  totalValuePaise: z.string(),          // stringified bigint — total stock value
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().nullable(),
});
export const getOutput = warehouseRow;

export const updateInput = baseFields.partial().extend({ id: uuid });
export const updateOutput = z.object({ id: uuid });

export const listInput = z.object({
  includeInactive: z.boolean().default(false),
});
export const listOutput = z.object({ warehouses: z.array(warehouseRow) });

// Derived TS types
export type CreateInput = z.infer<typeof createInput>;
export type CreateOutput = z.infer<typeof createOutput>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type UpdateInput = z.infer<typeof updateInput>;
export type UpdateOutput = z.infer<typeof updateOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type WarehouseRow = z.infer<typeof warehouseRow>;
